import React, { use, useEffect, useState } from 'react';

import Constant from '@core/app/actions/beambox/constant';
import OptionPanelIcons from '@core/app/icons/option-panel/OptionPanelIcons';
import { useIsMobile } from '@core/app/stores/screenStore';
import { useStorageStore } from '@core/app/stores/storageStore';
import { getSVGAsync } from '@core/helpers/svg-editor-helper';
import useI18n from '@core/helpers/useI18n';
import type ISVGCanvas from '@core/interfaces/ISVGCanvas';

import { ObjectPanelContext } from '../contexts/ObjectPanelContext';
import ObjectPanelController from '../contexts/ObjectPanelController';
import ObjectPanelItem from '../ObjectPanelItem';

import OptionsInput from './OptionsInput';
import styles from './RectOptions.module.scss';

let svgCanvas: ISVGCanvas;

getSVGAsync((globalSVG) => {
  svgCanvas = globalSVG.Canvas;
});

interface Props {
  elem: Element;
}

function RectOptions({ elem }: Props): React.JSX.Element {
  const lang = useI18n().beambox.right_panel.object_panel.option_panel;
  const isMobile = useIsMobile();
  const { dimensionValuesRef, updateDimensionValues } = use(ObjectPanelContext);
  const { rx: dimensionValuesRx } = dimensionValuesRef.current;
  const isInch = useStorageStore((state) => state.isInch);
  const [rx, setRx] = useState(dimensionValuesRx || 0);

  useEffect(() => {
    setRx(dimensionValuesRx || 0);
  }, [dimensionValuesRx]);

  const handleRoundedCornerChange = (val: null | number) => {
    if (val === null) return;

    val *= Constant.dpmm;
    setRx(val);
    svgCanvas.changeSelectedAttribute('rx', val, [elem]);
    updateDimensionValues({ rx: val });
  };

  const renderRoundCornerBlock = () => {
    return isMobile ? (
      <ObjectPanelItem.Number
        id="rounded-corner"
        label={lang.rounded_corner}
        min={0}
        updateValue={(val) => {
          handleRoundedCornerChange(val);
          ObjectPanelController.updateObjectPanel();
        }}
        value={rx / Constant.dpmm || 0}
      />
    ) : (
      <div className={styles['rounded-corner']} key="rounded-corner">
        <div className={styles.label} title={lang.rounded_corner}>
          <OptionPanelIcons.RoundedCorner />
        </div>
        <OptionsInput
          id="rounded-corner"
          isInch={isInch}
          min={0}
          onChange={handleRoundedCornerChange}
          precision={2}
          unit={isInch ? 'in' : 'mm'}
          value={rx / Constant.dpmm || 0}
          width={66}
        />
      </div>
    );
  };

  return isMobile ? renderRoundCornerBlock() : <div>{renderRoundCornerBlock()}</div>;
}

export default RectOptions;
